import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { AuthContext } from './AuthContext';
import { supabase } from '@/integrations/supabase/client';

const SubscriptionContext = createContext(null);

export const SubscriptionProvider = ({ children }) => {
  // Use context directly to handle case where AuthProvider isn't ready yet
  const auth = useContext(AuthContext);
  const user = auth?.user ?? null;
  const [profile, setProfile] = useState(null);
  const [usage, setUsage] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchProfile = useCallback(async () => {
    if (!user) {
      console.log('❌ No user logged in');
      setProfile(null);
      setUsage(null);
      setIsAdmin(false);
      setLoading(false);
      return;
    }

    console.log('🔍 Starting profile fetch for:', user.email);

    try {
      // Check admin status from database using is_admin() function
      let userIsAdmin = false;
      try {
        const { data: adminData, error: adminError } = await supabase.rpc('is_admin');
        if (!adminError && adminData === true) {
          userIsAdmin = true;
          console.log('✅ User is admin (from database)');
        }
      } catch (e) {
        console.log('⚠️ Could not check admin status:', e.message);
      }

      console.log('🔍 Admin check:', { email: user.email, isAdmin: userIsAdmin });

      // CRITICAL: Fetch REAL profile from database FIRST before setting anything
      // This prevents race condition where immediate profile lacks billing_type
      let dbProfile = null;
      try {
        // maybeSingle() instead of single() — .single() throws PGRST116/406
        // when 0 rows are found (e.g. row hasn't been created yet, or the
        // user_id in the session momentarily mismatches the DB during a
        // token refresh race). maybeSingle() returns null gracefully and we
        // fall back to a default profile, avoiding the infinite refetch loop
        // that spammed the console for users like Zack in Algeria.
        const { data: profileData, error: profileError } = await supabase
          .from('user_profiles')
          .select('*')
          .eq('user_id', user.id)
          .maybeSingle();
        
        if (!profileError && profileData) {
          dbProfile = profileData;
          console.log('✅ Database profile loaded:', {
            billing_type: profileData.billing_type,
            billing_status: profileData.billing_status,
            subscription_plan_id: profileData.subscription_plan_id
          });
        }
      } catch (e) {
        console.log('⚠️ Could not fetch database profile:', e.message);
      }

      // Create profile - prefer database data, fallback to defaults
      const immediateProfile = {
        id: user.id,
        user_id: user.id,
        email: user.email,
        name: userIsAdmin 
          ? `${user.email?.split('@')[0]} (Admin)` 
          : (dbProfile?.name || user.email?.split('@')[0] || 'User'),
        role: userIsAdmin ? 'admin' : (dbProfile?.role || 'user'),
        subscription_plan: dbProfile?.subscription_plan_id || dbProfile?.subscription_plan || (userIsAdmin ? 'institutional_ai' : null),
        subscription_plan_id: dbProfile?.subscription_plan_id || dbProfile?.subscription_plan || (userIsAdmin ? 'institutional_ai' : null),
        subscription_status: dbProfile?.subscription_status || 'active',
        analyses_used_this_month: dbProfile?.analyses_used_this_month || 0,
        is_active: dbProfile?.is_active ?? true,
        is_in_trial: dbProfile?.is_in_trial ?? false,
        trial_started_at: dbProfile?.trial_started_at || null,
        trial_ends_at: dbProfile?.trial_ends_at || null,
        // CRITICAL: Include billing fields to prevent race condition
        billing_type: dbProfile?.billing_type || null,
        billing_status: dbProfile?.billing_status || null,
        paid_until: dbProfile?.paid_until || null,
        // PROFILE FIELDS - Include all profile data from database
        first_name: dbProfile?.first_name || '',
        last_name: dbProfile?.last_name || '',
        phone: dbProfile?.phone || '',
        birthday: dbProfile?.birthday || '',
        personal_address: dbProfile?.personal_address || '',
        company_name: dbProfile?.company_name || '',
        company_address: dbProfile?.company_address || '',
        personal_profile: dbProfile?.personal_profile || '',
        company_profile: dbProfile?.company_profile || '',
        website: dbProfile?.website || '',
        social: dbProfile?.social || {},
        avatar_url: dbProfile?.avatar_url || '',
        ai_provider: dbProfile?.ai_provider || null,
        ai_model: dbProfile?.ai_model || null,
        // Onboarding flag — must be propagated so the 3-step flow can trigger
        first_login: dbProfile?.first_login ?? false,
        created_at: dbProfile?.created_at || new Date().toISOString(),
        updated_at: dbProfile?.updated_at || new Date().toISOString()
      };

      const immediateUsage = {
        count: 0,
        limit: userIsAdmin ? 1000 : 10,
        chartsCount: 0,
        chartsLimit: userIsAdmin ? 1000 : 5
      };

      // Set profile and usage immediately - NO DELAYS
      setProfile(immediateProfile);
      setUsage(immediateUsage);
      setIsAdmin(userIsAdmin);
      setLoading(false); // ✅ CRITICAL: Stop loading IMMEDIATELY

      console.log('✅ Immediate profile set, loading stopped');

      // Profile is already loaded with database data above
      console.log('✅ Profile set with billing data:', {
        billing_type: immediateProfile.billing_type,
        billing_status: immediateProfile.billing_status,
        subscription_plan_id: immediateProfile.subscription_plan_id
      });

    } catch (error) {
      console.error('💥 Critical error in fetchProfile:', error);
      
      // Even in case of critical error, provide minimal profile WITH billing fields
      const emergencyProfile = {
        id: user.id,
        user_id: user.id,
        email: user.email,
        name: user.email?.split('@')[0] || 'User',
        role: 'user',
        subscription_plan: null,
        subscription_plan_id: null,
        subscription_status: 'active',
        analyses_used_this_month: 0,
        is_active: true,
        is_in_trial: false,
        trial_started_at: null,
        trial_ends_at: null,
        billing_type: null,
        billing_status: null,
        paid_until: null
      };
      
      setProfile(emergencyProfile);
      setUsage({ count: 0, limit: 10, chartsCount: 0, chartsLimit: 5 });
      setIsAdmin(false);
      setLoading(false); // ✅ CRITICAL: Always stop loading

      console.log('🛡️ Emergency profile activated');
    }
    // Deps: user?.id + user?.email — NOT the whole user object.
    // Supabase returns a fresh user object on every TOKEN_REFRESHED even
    // when id is unchanged; depending on `user` recreated fetchProfile
    // on every refresh, re-firing the useEffect below and creating an
    // infinite fetch/refresh loop that ended in a 429 signout for Zack
    // (see log: 15+ token refreshes → 429 → SIGNED_OUT).
  }, [user?.id, user?.email]);

  // ✅ CRITICAL: Absolute maximum loading timeout
  useEffect(() => {
    if (loading) {
      const timeoutId = setTimeout(async () => {
        console.log('🚨 EMERGENCY: FORCING LOADING TO STOP');
        
        if (!profile && user) {
          // Check admin from database
          let emergencyAdmin = false;
          try {
            const { data } = await supabase.rpc('is_admin');
            emergencyAdmin = data === true;
          } catch (e) {
            console.log('Emergency admin check failed');
          }
          
          // Try to fetch real billing data even in emergency
          let dbBillingData = null;
          try {
            const { data: profileData } = await supabase
              .from('user_profiles')
              .select('billing_type, billing_status, subscription_plan_id, paid_until')
              .eq('user_id', user.id)
              .maybeSingle();
            if (profileData) dbBillingData = profileData;
          } catch (e) {
            console.log('Emergency billing data fetch failed');
          }
          
          const emergencyProfile = {
            id: user.id,
            user_id: user.id,
            email: user.email,
            name: emergencyAdmin ? `${user.email?.split('@')[0]} (Admin)` : (user.email?.split('@')[0] || 'User'),
            role: emergencyAdmin ? 'admin' : 'user',
            subscription_plan: dbBillingData?.subscription_plan_id || (emergencyAdmin ? 'institutional_ai' : null),
            subscription_plan_id: dbBillingData?.subscription_plan_id || (emergencyAdmin ? 'institutional_ai' : null),
            subscription_status: 'active',
            analyses_used_this_month: 0,
            is_active: true,
            is_in_trial: false,
            trial_started_at: null,
            trial_ends_at: null,
            // CRITICAL: Include billing fields
            billing_type: dbBillingData?.billing_type || null,
            billing_status: dbBillingData?.billing_status || null,
            paid_until: dbBillingData?.paid_until || null
          };
          
          setProfile(emergencyProfile);
          setIsAdmin(emergencyAdmin);
          setUsage({
            count: 0,
            limit: emergencyAdmin ? 1000 : 10,
            chartsCount: 0,
            chartsLimit: emergencyAdmin ? 1000 : 5
          });
        }
        
        setLoading(false); // ✅ FORCE STOP LOADING
        console.log('✅ EMERGENCY LOADING STOPPED - Dashboard should work now');
      }, 2500); // 2.5s — give the real admin/profile check time to win, so a
                // legitimate admin doesn't briefly see "Access Denied" on cold load.

      return () => clearTimeout(timeoutId);
    }
  }, [loading, profile, user]);

  useEffect(() => {
    if (user) {
      console.log('🔄 User changed, fetching profile...');
      fetchProfile();
    } else {
      console.log('👋 No user, clearing profile...');
      setProfile(null);
      setUsage(null);
      setIsAdmin(false);
      setLoading(false);
    }
  }, [user?.id, fetchProfile]);

  const canUseFeature = useCallback((feature) => {
    if (!profile) return false;
    if (isAdmin) return true;
    
    const plan = profile.subscription_plan;
    
    switch (feature) {
      case 'analysis':
        return usage && usage.count < usage.limit;
      case 'advanced_charts':
      case 'bloomberg_terminal':
        return plan === 'professional' || plan === 'enterprise';
      case 'voice_analysis':
      case 'api_access':
        return plan === 'enterprise';
      default:
        return true;
    }
  }, [profile, usage, isAdmin]);

  const value = {
    profile,
    userProfile: profile,
    usage,
    isAdmin,
    loading,
    profileLoading: loading,
    subscriptionLoading: loading,
    refreshProfile: fetchProfile,
    canUseFeature,
  };

  console.log('📊 SubscriptionContext state:', {
    hasProfile: !!profile,
    profileName: profile?.name,
    isAdmin,
    loading,
    userEmail: user?.email
  });

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};